export interface GainFormulaValidation {
  errors: string[];
  references: string[];
  unknownReferences: string[];
}

type Token =
  | { kind: 'num' }
  | { kind: 'ref' }
  | { kind: 'op'; value: '+' | '-' | '*' | '/' | '^' }
  | { kind: 'lparen' }
  | { kind: 'rparen' };

// Light client-side check mirroring the backend rules; the backend stays authoritative.
export function validateGainFormula(
  equation: string,
  knownParameters: ReadonlySet<string>,
): GainFormulaValidation {
  const references: string[] = [];
  const result: GainFormulaValidation = {
    errors: [],
    references,
    unknownReferences: [],
  };

  if (!equation.trim()) {
    result.errors.push('La formule est requise.');
    return result;
  }

  const tokens: Token[] = [];
  for (let i = 0; i < equation.length; i++) {
    const c = equation[i];

    if (/\s/.test(c)) continue;

    if (c === '[') {
      const end = equation.indexOf(']', i + 1);
      if (end < 0) {
        result.errors.push("Crochet fermant ']' manquant.");
        return result;
      }
      const name = equation.substring(i + 1, end);
      if (!name.trim()) {
        result.errors.push('Référence de paramètre vide « [] ».');
        return result;
      }
      if (name.includes('[')) {
        result.errors.push(`Crochet '[' imbriqué dans « [${name}] ».`);
        return result;
      }
      if (!references.includes(name)) references.push(name);
      tokens.push({ kind: 'ref' });
      i = end;
    } else if (c === ']') {
      result.errors.push("Crochet fermant ']' inattendu.");
      return result;
    } else if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < equation.length && /[0-9.]/.test(equation[j])) j++;
      const literal = equation.substring(i, j);
      if (!/^(\d+\.?\d*|\.\d+)$/.test(literal)) {
        result.errors.push(`Nombre invalide « ${literal} ».`);
        return result;
      }
      tokens.push({ kind: 'num' });
      i = j - 1;
    } else if (c === '(') {
      tokens.push({ kind: 'lparen' });
    } else if (c === ')') {
      tokens.push({ kind: 'rparen' });
    } else if ('+-*/^'.includes(c)) {
      tokens.push({ kind: 'op', value: c as '+' | '-' | '*' | '/' | '^' });
    } else {
      result.errors.push(
        `Caractère « ${c} » non autorisé. Seuls les nombres, + - * / ^, les parenthèses et les [paramètres] sont permis.`,
      );
      return result;
    }
  }

  const syntaxError = checkSyntax(tokens);
  if (syntaxError) result.errors.push(syntaxError);

  result.unknownReferences = references.filter((r) => !knownParameters.has(r));
  if (result.unknownReferences.length > 0) {
    result.errors.push(
      `Paramètre(s) inexistant(s) : ${result.unknownReferences
        .map((r) => `[${r}]`)
        .join(', ')}.`,
    );
  }

  return result;
}

// expr := term (('+'|'-') term)* ; term := power (('*'|'/') power)* ;
// power := unary ('^' power)? ; unary := '-' unary | primary ; primary := num | ref | '(' expr ')'
function checkSyntax(tokens: Token[]): string | null {
  let pos = 0;

  function fail(message: string): never {
    throw new Error(message);
  }

  const expr = (): void => {
    term();
    while (isOp('+') || isOp('-')) {
      pos++;
      term();
    }
  };

  const term = (): void => {
    power();
    while (isOp('*') || isOp('/')) {
      pos++;
      power();
    }
  };

  const power = (): void => {
    unary();
    if (isOp('^')) {
      pos++;
      power();
    }
  };

  const unary = (): void => {
    if (isOp('-')) {
      pos++;
      unary();
      return;
    }
    primary();
  };

  const primary = (): void => {
    const token = tokens[pos];
    if (!token) fail('La formule est incomplète (opérande attendu).');
    if (token.kind === 'num' || token.kind === 'ref') {
      pos++;
      return;
    }
    if (token.kind === 'lparen') {
      pos++;
      expr();
      if (tokens[pos]?.kind !== 'rparen')
        fail("Parenthèse fermante ')' manquante.");
      pos++;
      return;
    }
    if (token.kind === 'rparen') fail("Parenthèse fermante ')' inattendue.");
    fail(`Opérateur « ${(token as { value: string }).value} » inattendu.`);
  };

  const isOp = (value: string): boolean => {
    const token = tokens[pos];
    return token?.kind === 'op' && token.value === value;
  };

  try {
    expr();
    if (pos < tokens.length) {
      const token = tokens[pos];
      fail(
        token.kind === 'rparen'
          ? "Parenthèse fermante ')' inattendue."
          : 'Opérateur manquant entre deux valeurs.',
      );
    }
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}
